import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ApiError,
  SEARCH_QUERY_MIN_LENGTH,
  boardPath,
  type BoardPageBoard,
  type SearchBoardResult,
  type SearchCardResult,
} from '@molt/shared';
import { Search } from 'lucide-react';
import {
  Button,
  Combobox,
  EmptyState,
  IconButton,
  Notice,
  Skeleton,
  type ComboboxGroup,
  type ComboboxOption,
} from '@/components';
import { cn } from '@/lib/cn';
import { globalSearchQueryOptions, searchJumpToQueryOptions } from '@/lib/search';
import { useInterfaceLanguage } from '@/lib/interface-language';
import { OperatorHelp } from './OperatorHelp';

export type GlobalSearchNavigate = (href: string) => void;

export interface GlobalSearchProps {
  readonly compact?: boolean;
  readonly onNavigate: GlobalSearchNavigate;
}

interface SearchOption extends ComboboxOption {
  readonly kind: 'card' | 'board';
  readonly href: string;
  readonly title: string;
  readonly context: string;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toLowerCase();
  return (
    tag === 'input' ||
    tag === 'textarea' ||
    tag === 'select' ||
    target.isContentEditable ||
    target.closest('[contenteditable="true"], [role="textbox"]') !== null
  );
}

function cardOption(card: SearchCardResult): SearchOption {
  return {
    id: `card-${card.id}`,
    kind: 'card',
    href: card.path,
    label: `${card.title} ${card.board.title} ${card.list.title}`,
    title: card.title,
    context: `${card.board.title} · ${card.list.title}`,
  };
}

function boardOption(board: SearchBoardResult, boardContext: string): SearchOption {
  return {
    id: `board-${board.id}`,
    kind: 'board',
    href: board.path,
    label: board.title,
    title: board.title,
    context: boardContext,
  };
}

function jumpBoardOption(
  board: BoardPageBoard,
  scope: 'starred' | 'recent',
  boardContext: string,
): SearchOption {
  return {
    id: `jump-board-${scope}-${board.id}`,
    kind: 'board',
    href: boardPath(board.shortLink, board.slug),
    label: board.title,
    title: board.title,
    context: boardContext,
  };
}

function fullSearchHref(query: string): string {
  const trimmed = query.trim();
  const params = new URLSearchParams({ q: trimmed });
  return `/search?${params.toString()}`;
}

function optionGroups(
  cards: readonly SearchCardResult[],
  boards: readonly SearchBoardResult[],
  labels: { cards: string; boards: string; boardContext: string },
): ComboboxGroup<SearchOption>[] {
  return [
    { id: 'cards', label: labels.cards, options: cards.map(cardOption) },
    {
      id: 'boards',
      label: labels.boards,
      options: boards.map((board) => boardOption(board, labels.boardContext)),
    },
  ];
}

function jumpOptionGroups(
  starred: readonly BoardPageBoard[],
  recent: readonly BoardPageBoard[],
  labels: { starred: string; recent: string; boardContext: string },
): ComboboxGroup<SearchOption>[] {
  return [
    {
      id: 'starred-boards',
      label: labels.starred,
      options: starred.map((board) =>
        jumpBoardOption(board, 'starred', labels.boardContext),
      ),
    },
    {
      id: 'recently-viewed-boards',
      label: labels.recent,
      options: recent.map((board) =>
        jumpBoardOption(board, 'recent', labels.boardContext),
      ),
    },
  ];
}

export function GlobalSearch({
  compact = false,
  onNavigate,
}: GlobalSearchProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const [value, setValue] = useState('');
  const [compactOpen, setCompactOpen] = useState(!compact);
  const hostRef = useRef<HTMLDivElement>(null);
  const trimmed = value.trim();
  const queryEnabled = trimmed.length >= SEARCH_QUERY_MIN_LENGTH;
  const jumpToEnabled = trimmed.length === 0;
  const jumpTo = useQuery({
    ...searchJumpToQueryOptions(),
    enabled: jumpToEnabled,
  });
  const results = useQuery({
    ...globalSearchQueryOptions(queryEnabled ? trimmed : '___'),
    enabled: queryEnabled,
  });
  const groups = useMemo(
    () =>
      jumpToEnabled
        ? jumpOptionGroups(jumpTo.data?.starred ?? [], jumpTo.data?.recent ?? [], {
            starred: t('search.starredBoardsGroup'),
            recent: t('search.recentBoardsGroup'),
            boardContext: t('search.boardContext'),
          })
        : optionGroups(results.data?.cards ?? [], results.data?.boards ?? [], {
            cards: t('search.cardsGroup'),
            boards: t('search.boardsGroup'),
            boardContext: t('search.boardContext'),
          }),
    [jumpTo.data, jumpToEnabled, results.data, t],
  );
  const canOpenFullSearch = trimmed.length > 0;

  function focusInput() {
    const input = hostRef.current?.querySelector<HTMLInputElement>(
      'input[role="combobox"]',
    );
    input?.focus();
  }

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const isSlash = event.key === '/' || event.code === 'Slash';
      if (!isSlash || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableTarget(event.target)) return;
      event.preventDefault();
      setCompactOpen(true);
      window.setTimeout(focusInput, 0);
    }

    document.addEventListener('keydown', onKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', onKeyDown, { capture: true });
  }, []);

  const emptyState = jumpToEnabled ? (
    jumpTo.isLoading ? (
      <div aria-label={t('search.loadingBoardShortcuts')} aria-busy="true">
        <Skeleton shape="row" count={2} />
      </div>
    ) : jumpTo.isError ? (
      <Notice
        tone="bad"
        title={t('search.boardShortcutsFailedTitle')}
        body={t('search.tryAgainMoment')}
      />
    ) : (
      <EmptyState size="inline" tone="neutral" title={t('search.noShortcuts')} />
    )
  ) : !queryEnabled ? (
    <EmptyState
      size="inline"
      tone="neutral"
      title={t('search.keepTyping', { count: SEARCH_QUERY_MIN_LENGTH })}
    />
  ) : results.isLoading ? (
    <div aria-label={t('search.searching')} aria-busy="true">
      <Skeleton shape="row" count={3} />
    </div>
  ) : results.error instanceof ApiError && results.error.code === 'validation_failed' ? (
    <EmptyState
      size="inline"
      tone="neutral"
      title={t('search.keepTyping', { count: SEARCH_QUERY_MIN_LENGTH })}
    />
  ) : results.isError ? (
    <Notice
      tone="bad"
      title={t('search.loadFailedTitle')}
      body={t('search.tryAgainMoment')}
    />
  ) : (
    <EmptyState
      size="inline"
      tone="neutral"
      title={t('search.noMatches', { query: trimmed })}
    />
  );

  return (
    <div ref={hostRef} className="relative">
      {compact ? (
        <IconButton
          label={t('search.label')}
          className="lg:hidden"
          onClick={() => {
            setCompactOpen(true);
            window.setTimeout(focusInput, 0);
          }}
        >
          <Search aria-hidden="true" className="size-4" />
        </IconButton>
      ) : null}

      <div
        className={cn(
          compact ? 'hidden lg:block' : 'block',
          compact && compactOpen
            ? 'absolute right-0 top-0 z-50 block w-[min(20rem,calc(100vw-2rem))] lg:static lg:w-auto'
            : null,
        )}
      >
        <Combobox<SearchOption>
          label={t('search.label')}
          hideLabel
          placeholder={t('search.label')}
          value={value}
          onChange={setValue}
          groups={groups}
          minLength={SEARCH_QUERY_MIN_LENGTH}
          openWhenEmpty
          loading={jumpToEnabled ? jumpTo.isFetching : results.isFetching}
          emptyState={emptyState}
          leadingIcon={<Search aria-hidden="true" className="size-4" />}
          onActivate={(option) => {
            onNavigate(option.href);
            setCompactOpen(!compact);
          }}
          onSubmit={(query) => {
            const next = query.trim();
            if (next.length === 0) return;
            onNavigate(fullSearchHref(next));
            setCompactOpen(!compact);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              const input = event.currentTarget;
              window.setTimeout(() => {
                input.blur();
                if (compact) setCompactOpen(false);
              }, 0);
            }
          }}
          renderOption={(option) => (
            <span className="block min-w-0">
              <span className="block truncate font-medium">{option.title}</span>
              <span className="block truncate text-xs text-current opacity-75">
                {option.context}
              </span>
            </span>
          )}
          footer={
            <div className="space-y-2">
              <OperatorHelp query={value} onQueryChange={setValue} compact={compact} />
              {canOpenFullSearch ? (
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  className="w-full justify-start"
                  onClick={() => {
                    onNavigate(fullSearchHref(trimmed));
                    setCompactOpen(!compact);
                  }}
                >
                  {t('search.advanced')}
                </Button>
              ) : null}
            </div>
          }
        />
        {compact && compactOpen ? (
          <Button
            type="button"
            size="sm"
            variant="quiet"
            className="mt-1 w-full lg:hidden"
            onClick={() => setCompactOpen(false)}
          >
            {t('search.close')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
